//SavedResultsModal.tsx
import React from 'react'
import { Modal, List, Button, Space, Tag, Typography, Badge, Popconfirm } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { TestErrorEntry } from './types'
import LoadingCard from '../../components/LoadingCard'

const { Text } = Typography

interface TestResultDB {
  id: string
  test_name: string
  language: string
  instruction_id: string
  instruction_name: string
  instruction_text: string
  text_id: string
  text_name: string
  text_content: string
  models: string[]
  evaluation_models?: string[]
  run_number: number
  total_runs: number
  translation_results: any[]
  evaluation_results: any[]
  created_at: string
  discarded_evaluations: string[]
  translation_errors?: TestErrorEntry[]
  evaluation_errors?: TestErrorEntry[]
}

interface SavedResultsModalProps {
  visible: boolean
  loading: boolean
  dbResults: TestResultDB[]
  onClose: () => void
  onLoad: (item: TestResultDB) => void
  onDelete: (id: string) => Promise<void> | void
  // New pagination props
  totalCount?: number
  pageSize?: number
  currentPage?: number
  onPageChange?: (page: number, pageSize: number) => void
}

const SavedResultsModal: React.FC<SavedResultsModalProps> = ({
  visible,
  loading,
  dbResults,
  onClose,
  onLoad,
  onDelete,
  totalCount = 0,
  pageSize = 10,
  currentPage = 1,
  onPageChange,
}) => {
  return (
    <Modal title="Database Test Results" visible={visible} onCancel={onClose} footer={null} width={1000}>
      {loading ? (
        <div className="loading-card-wrapper" style={{ padding: 40 }}>
          <LoadingCard label="Loading saved results..." />
        </div>
      ) : (
        <List
          dataSource={dbResults}
          pagination={{
            total: totalCount,
            pageSize,
            current: currentPage,
            onChange: (page, size) => onPageChange && onPageChange(page, size || pageSize),
            showTotal: (total) => `${total.toLocaleString()} results`,
          }}
          renderItem={(item: TestResultDB) => {
            const isMultiRun = item.total_runs > 1
            const translationErrorCount = item.translation_errors?.length ?? 0
            const evaluationErrorCount = item.evaluation_errors?.length ?? 0
            const totalErrors = translationErrorCount + evaluationErrorCount
            return (
              <List.Item
                actions={[
                  <Button key="load" type="primary" onClick={() => onLoad(item)}>
                    {isMultiRun ? 'Load Averaged' : 'Load'}
                  </Button>,
                  <Popconfirm
                    key="delete"
                    title="Are you sure you want to delete this result?"
                    onConfirm={() => onDelete(item.id)}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{item.test_name}</Text>
                      <Tag color="blue">{item.language}</Tag>
                      {isMultiRun ? (
                        <Badge count={`${item.total_runs} runs`} style={{ backgroundColor: '#52c41a' }} />
                      ) : (
                        <Tag color="orange">
                          Run {item.run_number}/{item.total_runs}
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary">Date: {new Date(item.created_at).toLocaleString()}</Text>
                      <Text type="secondary">Instruction: {item.instruction_name}</Text>
                      <Text type="secondary">Text: {item.text_name}</Text>
                      <div>
                        <Text type="secondary">Models: </Text>
                        {item.models.map(model => (
                          <Tag key={model}>{model}</Tag>
                        ))}
                      </div>
                      <div>
                        <Text type="secondary">Evaluators: </Text>
                        {(item.evaluation_models || []).map(model => (
                          <Tag key={`eval-${model}`} color="purple">{model}</Tag>
                        ))}
                        {!item.evaluation_models?.length && <Text type="secondary">N/A</Text>}
                      </div>
                      {totalErrors > 0 && (
                        <Tag color="red">{totalErrors} error{totalErrors === 1 ? '' : 's'}</Tag>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )
          }}
        />
      )}
    </Modal>
  )
}

export default SavedResultsModal
